import { defineStore } from 'pinia';
import type { Serializer } from '@vueuse/core';
import { useStorage } from '@vueuse/core';
import { getEndOfCurrentMonth } from 'src/utils/dateUtils';
import type { ReceiptInvoiceType } from 'src/api';
import { ReceiptFilterPaymentStatus, ReceiptPaymentType } from 'src/api';
import type { ToggleStateFilterValue } from 'components/office/receipts/list/constants';
import { computed } from 'vue';

const serializeDate = (date: Date): string => date.toISOString();
const deserializeDate = (dateString: string): Date => new Date(dateString);

const getStartOfMonth = (): Date => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const serializeInvoiceTypesArray = (arr?: ReceiptInvoiceType[]): string =>
  JSON.stringify(arr);
const deserializeInvoiceTypesArray = (
  arrString: string
): ReceiptInvoiceType[] | undefined => {
  const arr = JSON.parse(arrString) as ReceiptInvoiceType[] | undefined;
  return arr && arr.length > 0 ? arr : undefined;
};

const invoiceSerializer: Serializer<ReceiptInvoiceType[] | undefined> = {
  read: deserializeInvoiceTypesArray,
  write: serializeInvoiceTypesArray,
};

const dateSerializer: Serializer<Date> = {
  read: deserializeDate,
  write: serializeDate,
};

export const useReceiptFilters = defineStore('useReceiptFilters', () => {
  const searchTerms = useStorage('receipt-list-search', '');
  const createdFrom = useStorage(
    'receipt-list-created-from',
    getStartOfMonth,
    undefined,
    { serializer: dateSerializer }
  );
  const createdTo = useStorage<Date>(
    'receipt-list-created-to',
    getEndOfCurrentMonth,
    undefined,
    { serializer: dateSerializer }
  );
  const userFilterValue = useStorage<string | undefined>(
    'receipt-list-user-filter-value',
    undefined
  );
  const toggleOfficeLocation = useStorage<string | undefined>(
    'receipt-list-office-location-value',
    undefined
  );

  const togglePaymentFilterValue = useStorage<ToggleStateFilterValue>(
    'receipt-list-payment-status',
    ReceiptFilterPaymentStatus.ALL
  );

  const togglePaymentStatus = computed(() =>
    togglePaymentFilterValue.value === 'not-issued'
      ? undefined
      : togglePaymentFilterValue.value
  );

  const togglePaymentTypes = useStorage<ReceiptPaymentType[]>(
    'receipt-list-payment-types',
    [
      ReceiptPaymentType.CASH,
      ReceiptPaymentType.CARD,
      ReceiptPaymentType.BANK_TRANSFER,
      ReceiptPaymentType.WEBPAY,
    ]
  );
  const toggleInvoiceTypes = useStorage<ReceiptInvoiceType[] | undefined>(
    'receipt-list-invoice-types',
    [],
    undefined,
    { serializer: invoiceSerializer }
  );

  function resetFilters(): void {
    searchTerms.value = '';
    createdFrom.value = getStartOfMonth();
    createdTo.value = getEndOfCurrentMonth();
    userFilterValue.value = undefined;
    toggleOfficeLocation.value = undefined;
    togglePaymentFilterValue.value = ReceiptFilterPaymentStatus.ALL;
    togglePaymentTypes.value = [
      ReceiptPaymentType.CASH,
      ReceiptPaymentType.CARD,
      ReceiptPaymentType.BANK_TRANSFER,
      ReceiptPaymentType.WEBPAY,
    ];
    toggleInvoiceTypes.value = undefined;
  }

  return {
    searchTerms,
    createdFrom,
    createdTo,
    userFilterValue,
    toggleOfficeLocation,
    togglePaymentFilterValue,
    togglePaymentTypes,
    toggleInvoiceTypes,
    togglePaymentStatus,
    resetFilters,
  };
});
